import React, { useEffect, useMemo, useRef, useState } from "react";
import { nat } from "./utils";
import CellSearch from "./CellSearch";

type Props = {
  depotIds: string[];
  selectedDepot: string;
  onChangeDepot: (v: string) => void;

  rOptions: string[];
  kOptions: string[];
  pOptions: string[];
  selR: string[];
  selK: string[];
  selP: string[];
  setSelR: (v: string[]) => void;
  setSelK: (v: string[]) => void;
  setSelP: (v: string[]) => void;
  onClear: () => void;

  codes: string[];
  onJump: (code: string) => void;
};

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const view = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.toLowerCase().includes(s));
  }, [options, q]);

  const toggle = (v: string) =>
    selected.includes(v)
      ? onChange(selected.filter((x) => x !== v))
      : onChange([...selected, v]);

  return (
    <div className="wm-dd" ref={ref}>
      <button
        type="button"
        className="wm-dd-btn"
        onClick={() => setOpen((o) => !o)}>
        {label}
        {!!selected.length && (
          <span className="wm-dd-count">{selected.length}</span>
        )}
      </button>
      {open && (
        <div className="wm-dd-menu">
          <input
            className="wm-dd-search"
            placeholder={`Caută ${label}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="wm-dd-list">
            {view.length === 0 && (
              <div className="wm-dd-empty">Nimic găsit</div>
            )}
            {view.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <label key={opt} className="wm-dd-item">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt)}
                  />
                  <span>{opt}</span>
                </label>
              );
            })}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              className="wm-dd-clear"
              onClick={() => onChange([])}>
              Clear {label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const FiltersBar: React.FC<Props> = ({
  depotIds,
  selectedDepot,
  onChangeDepot,
  rOptions,
  kOptions,
  pOptions,
  selR,
  selK,
  selP,
  setSelR,
  setSelK,
  setSelP,
  onClear,
  codes,
  onJump,
}) => {
  const r = useMemo(() => [...rOptions].sort(nat), [rOptions]);
  const k = useMemo(() => [...kOptions].sort(nat), [kOptions]);
  const p = useMemo(() => [...pOptions].sort(nat), [pOptions]);

  return (
    <div className="wm-toolbar" style={{ gap: 12, padding: "8px 10px" }}>
      <div className="wm-field">
        <label className="wm-field-label" htmlFor="depot">
          Depozit
        </label>
        <select
          id="depot"
          className="wm-select"
          value={selectedDepot}
          onChange={(e) => onChangeDepot(e.target.value)}>
          <option value="ALL">All</option>
          {depotIds.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <CellSearch codes={codes} onSelect={onJump} />

      <span className="wm-field-label" style={{ marginLeft: 6 }}>
        Filtre:
      </span>
      <MultiSelectDropdown
        label="R"
        options={r}
        selected={selR}
        onChange={setSelR}
      />
      <MultiSelectDropdown
        label="K"
        options={k}
        selected={selK}
        onChange={setSelK}
      />
      <MultiSelectDropdown
        label="P"
        options={p}
        selected={selP}
        onChange={setSelP}
      />
      {(selR.length || selK.length || selP.length) > 0 && (
        <button type="button" className="wm-chip" onClick={onClear}>
          Clear all
        </button>
      )}
    </div>
  );
};

export default FiltersBar;
